import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import { useMemo } from "react";
import { type FragmentType, graphql, useFragment } from "~/gql/runtime";
import { Wrap } from "~/features/ui/wrap";
import { FilterBar, type Filter } from "~/features/filter/filter-bar";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
const DEFAULT_COUNTRY = {
  label: "All Countries",
  value: ""
};
const DEFAULT_STATE = {
  label: "All States",
  value: ""
};
const COUNTRIES_WITH_STATES = ["United States", "Canada"];
export const distributorsFiltersFragment = graphql(`
  fragment DistributorsFilters on Query {
    distributors: entries(section: "distributor", relatedTo: $relatedTo) {
      ... on distributor_default_Entry {
        locations: baseLocations {
          ... on baseLocations_location_BlockType {
            location {
              parts {
                country
                state
              }
              lat
              lng
            }
          }
        }
      }
    }
  }
`);
type Props = FragmentType<typeof distributorsFiltersFragment> & {
  onChange: (type: string, val: string) => void;
  mode: Mode;
  label?: string;
  theme: Theme;
  value: {
    country: string;
    state?: string;
  };
};
export function DistributorsFilters(props: Props) {
  const {
    onChange,
    value,
    mode,
    theme,
    label,
    ...rest
  } = props;
  const {
    distributors = []
  } = useFragment(distributorsFiltersFragment, rest);
  const themeStyle = getThemeStyle({
    mode,
    theme
  });
  const {
    countryOptions,
    stateOptions
  } = useMemo(() => {
    const countryStateMap: Record<string, Set<string>> = {};
    distributors.flatMap(distributor => distributor.locations ?? []).reduce((map: Record<string, Set<string>>, location) => {
      if (!location) return map;
      const parts = location.location?.parts;
      if (parts?.country) {
        if (!map[parts.country]) {
          map[parts.country] = new Set();
        }
        if (parts.state) {
          map[parts.country]!.add(parts.state);
        }
      }
      return map;
    }, countryStateMap);
    const countryOptions = [DEFAULT_COUNTRY, ...Object.keys(countryStateMap).sort((a, b) => a.localeCompare(b)).map(country => ({
      label: country,
      value: country
    }))];
    const selectedCountry = value.country;
    const stateOptions = selectedCountry ? [DEFAULT_STATE, ...Array.from(countryStateMap[selectedCountry] || []).sort((a, b) => a.localeCompare(b)).map(state => ({
      label: state,
      value: state
    }))] : [DEFAULT_STATE];
    return {
      countryOptions,
      stateOptions
    };
  }, [distributors, value.country]);
  const filters: Filter[] = [{
    title: "Country",
    options: countryOptions,
    type: "dropdown",
    value: value.country,
    style: {
      "width": "100%",
      "minWidth": "15ch",
      "flex": "1 1 0%",
      ">button": {
        "width": "100% !important",
        "flex": "1 1 0%"
      }
    }
  }, ...(value.country && COUNTRIES_WITH_STATES.includes(value.country) ? [{
    title: "State",
    options: stateOptions,
    type: "dropdown" as const,
    value: value.state || "",
    style: {
      "minWidth": "15ch",
      "flex": "1 1 0%",
      ">button": {
        "width": "100% !important",
        "flex": "1 1 0%"
      }
    }
  }] : [])];
  return <_StyledDiv $_css={[{
    "marginLeft": "0",
    "marginRight": "0",
    "width": "100%",
    "maxWidth": "none",
    "borderTopWidth": "1px"
  }, themeStyle.background, themeStyle.border]}>
      <Wrap mode={mode}>
        <_StyledDiv2 $_css2={[{
        "marginLeft": "auto",
        "marginRight": "auto",
        "maxWidth": "1920px",
        "@media (min-width: 1024px)": {
          "height": "8.8rem"
        }
      }, themeStyle.background, themeStyle.border]}>
          <_StyledDiv3 $_css3={[{
          "position": "relative",
          "zIndex": "10",
          "paddingLeft": "2.4rem",
          "paddingRight": "2.4rem",
          "paddingTop": "2.4rem",
          "paddingBottom": "2.4rem",
          "@media (min-width: 1024px)": {
            "paddingLeft": "4rem",
            "paddingRight": "4rem"
          }
        }]}>
            <_StyledDiv4>
              <_StyledFilterBar filters={filters} onChange={onChange} />
            </_StyledDiv4>
          </_StyledDiv3>
        </_StyledDiv2>
      </Wrap>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1afiwm3-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1afiwm3-1"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-1afiwm3-2"
})`${p => p.$_css3}`;
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-1afiwm3-3"
})({
  "display": "flex",
  "alignItems": "center",
  "gap": "1.6rem",
  "fontSize": "1.6rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.032rem",
  "fontWeight": "400"
});
var _StyledFilterBar = /*#__PURE__*/_styled5(FilterBar).withConfig({
  displayName: "distributors-filters___StyledFilterBar",
  componentId: "sc-2gyw8j-0"
})({
  "width": "100%",
  "@media (min-width: 640px)": {
    "width": "auto"
  }
});